import { useMemo } from 'react';
import { Extensions } from '@tiptap/core';

import { Text } from '@tiptap/extension-text';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';

import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Strike } from '@tiptap/extension-strike';
import { Underline } from '@tiptap/extension-underline';
import { HardBreak } from '@tiptap/extension-hard-break';

import { Blockquote } from '@tiptap/extension-blockquote';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Dropcursor } from '@tiptap/extension-dropcursor';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { History } from '@tiptap/extension-history';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import { ListItem } from '@tiptap/extension-list-item';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { DaTextAlign } from '../../extensions/extension-alignment/alignment';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import { Heading } from '../../extensions/extension-heading/heading';
import { CharacterCount } from '@tiptap/extension-character-count';

import { Paste } from '../../extensions/extension-paste/paste';

import { Anchor } from '../../extensions/extension-anchor/anchor';
import { Spinner } from '../../extensions/extension-spinner/spinner';
import { Emote } from '../../extensions/extension-emote/emote';
import { DaDeviation } from '../../extensions/extension-deviation/deviation';
import { DndUpload } from '../../extensions/extension-dnd-upload/dnd-upload';
import { Mention as DaMention } from '../../extensions/extension-mention/mention';
import { Link } from '../../extensions/extension-link/link';
import { Gif } from '../../extensions/extension-gif/gif';
import LinkMenuHandler from '../../extensions/extension-link-menu-handler/linkMenuHandler';
import { DaVideo } from '../../extensions/extension-video/daVideo';
import BlockDrag from '../../extensions/extension-block-drag/draggable';
import { Hashtag } from '../../extensions/extension-hashtag/hashtag';

import { Placeholder } from '@tiptap/extension-placeholder';
import { DaGallery } from '../../extensions/extension-gallery/gallery';
import { Indent } from '../../extensions/extension-indent/indent';
import { isTouch } from '../utils/browser';
import { ExtensionsConfigOptions, ExtensionsRuntimeOptions } from './types';
import { DaMuro } from '../../extensions/extension-muro/muro';
import { DaDeviationThumb } from '../../extensions/extension-deviation-thumb/deviation-thumb';
import { DaKeyboardSubmit } from '../../extensions/extension-keyboard-submit/keyboard-submit';
import ListKeymap from '@tiptap/extension-list-keymap';
import { SingleLine } from '../../extensions/extension-singleline/singleline';
import { DaDamz } from '../../extensions/extension-damz/damz';
import { DaExclusiveAvatar } from '../../extensions/extension-exclusive-avatar/exclusive-avatar';

export const factoryExtensions = (
  configOptions: ExtensionsConfigOptions,
  runtimeOptions: ExtensionsRuntimeOptions
): Extensions => {
  const {
    placeholder,
    placeholderOptions,
    characterCountOptions,
    presetRuntimeOptions,
  } = runtimeOptions;

  // Defaults
  const extensions = [
    Document,
    Text,
    Paragraph,
    TextStyle, // does nothing used by other text extensions
    Dropcursor,
    Gapcursor,
    History,
    ListItem,
    ListKeymap, // Fixes list ux with backspace
  ];

  if (configOptions.damz) {
    extensions.push(DaDamz.configure(presetRuntimeOptions?.damz));
  }

  if (!configOptions.disableHardBreak) {
    extensions.push(HardBreak);
  }

  if (configOptions.indent) {
    extensions.push(
      Indent.configure(
        configOptions.indent !== true ? configOptions.indent : undefined
      )
    );
  } else {
    extensions.push(Indent);
  }

  if (configOptions.paste) {
    extensions.push(Paste.configure(configOptions.paste));
  } else {
    extensions.push(Paste);
  }

  if (configOptions.bold) {
    extensions.push(Bold);
  }

  if (configOptions.italic) {
    extensions.push(Italic);
  }

  if (configOptions.strikethrough) {
    extensions.push(Strike);
  }

  if (configOptions.underline) {
    extensions.push(Underline);
  }

  if (configOptions.heading) {
    extensions.push(Heading.configure({ levels: [2, 3, 4] }));
  }

  if (configOptions.blockDragging && !isTouch()) {
    extensions.push(BlockDrag);
  }

  if (configOptions.blockquote) {
    extensions.push(Blockquote);
  }

  if (configOptions.anchor) {
    extensions.push(Anchor);
  }

  if (configOptions.spinner) {
    extensions.push(Spinner);
  }

  if (configOptions.bulletList) {
    extensions.push(BulletList);
  }

  if (configOptions.orderedList) {
    extensions.push(OrderedList);
  }

  if (configOptions.emote) {
    extensions.push(Emote);
  }

  if (configOptions.gif) {
    extensions.push(
      Gif.configure(configOptions.gif !== true ? configOptions.gif : undefined)
    );
  }

  if (configOptions.daVideo) {
    extensions.push(
      DaVideo.configure(
        configOptions.daVideo !== true ? configOptions.daVideo : undefined
      )
    );
  }

  if (configOptions.horizontalRuler) {
    extensions.push(
      HorizontalRule.configure({ HTMLAttributes: { 'data-ruler': '1' } })
    );
  }

  if (configOptions.textAlignment) {
    extensions.push(
      DaTextAlign.configure({
        types: ['heading', 'paragraph'],
      })
    );
  }

  if (configOptions.textColor) {
    extensions.push(Color);
  }

  if (configOptions.textHighlight) {
    extensions.push(Highlight.configure({ multicolor: true }));
  }

  if (configOptions.textFont) {
    extensions.push(FontFamily);
  }

  if (configOptions.link) {
    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        protocols: ['https', 'http', 'mailto'],
        validate(url) {
          return /^(#|http|mailto)/.test(url);
        },
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow ugc',
          target: '_blank',
        },
      }),
      LinkMenuHandler
    );
  }

  if (configOptions.deviation) {
    extensions.push(
      DaDeviation.configure(
        configOptions.deviation !== true ? configOptions.deviation : undefined
      )
    );
    if (configOptions.allowUploads) {
      extensions.push(DndUpload);
    }
    if (configOptions.deviationThumb) {
      extensions.push(
        DaDeviationThumb.configure(
          configOptions.deviationThumb !== true
            ? configOptions.deviationThumb
            : undefined
        )
      );
    }
  }

  if (configOptions.gallery) {
    extensions.push(
      DaGallery.configure(
        configOptions.gallery !== true ? configOptions.gallery : undefined
      )
    );
  }

  if (configOptions.mention) {
    extensions.push(
      DaMention.configure({ HTMLAttributes: { class: 'mention' } })
    );
  }

  // Make subscript and superscript mutually exclusive
  // https://github.com/ueberdosis/tiptap/pull/1436#issuecomment-1031937768
  // const CustomSubscript = Subscript.extend({
  //  excludes: "superscript",
  // });

  // const CustomSuperscript = Superscript.extend({
  //  excludes: "subscript",
  // });

  if (configOptions.hashtag) {
    extensions.push(Hashtag);
  }

  if (configOptions.exclusiveAvatar) {
    extensions.push(DaExclusiveAvatar);
  }

  if (configOptions.muro) {
    extensions.push(DaMuro.configure());
  }

  if (characterCountOptions) {
    extensions.push(CharacterCount.configure(characterCountOptions));
  }

  if (placeholder && !placeholderOptions) {
    extensions.push(Placeholder.configure({ placeholder }));
  }
  if (placeholderOptions) {
    extensions.push(Placeholder.configure(placeholderOptions));
  }

  if (configOptions.modEnterSubmit) {
    extensions.push(DaKeyboardSubmit.configure({}));
  }

  if (configOptions.singleLine) {
    extensions.push(SingleLine.configure());
  }

  return extensions;
};

export const useExtensions = (
  configOptions: ExtensionsConfigOptions,
  runtimeOptions: ExtensionsRuntimeOptions
) => {
  const {
    placeholder,
    placeholderOptions,
    characterCountOptions,
    presetRuntimeOptions,
  } = runtimeOptions;

  const exts = useMemo(() => {
    return factoryExtensions(configOptions, {
      placeholder,
      placeholderOptions,
      characterCountOptions,
      presetRuntimeOptions,
    });
  }, [
    configOptions,
    placeholder,
    placeholderOptions,
    characterCountOptions,
    presetRuntimeOptions,
  ]);

  return exts;
};
